import moment from 'moment';
import { useCallback, useState } from 'react';

import { PublicHolidayDto } from '~/services/Http/client.people.generated';
import {
  UseGetPublicHolidaysArgs,
  useGetPublicHolidays,
} from '~/services/Http/hooks';
import { getYearsOfDatesInRange } from '~/utils/helper';

type UsePublicHolidaysInPickerOutput = {
  publicHolidays: PublicHolidayDto[];
  publicHolidaysFromYears: number[];
  setViewedRange: (props: {
    pickerMonth: Date;
    pickerCalendars?: number;
  }) => void;
  setSelectedRange: (props: { rangeStart: Date; rangeEnd: Date }) => void;
};
export const usePublicHolidaysInPicker = (
  args: UseGetPublicHolidaysArgs,
): UsePublicHolidaysInPickerOutput => {
  const { years, ...passArgs } = args;
  const [publicHolidaysFromYears, setPublicHolidaysFromYears] = useState<
    number[]
  >(years || [new Date().getFullYear()]);

  const { data: publicHolidays } = useGetPublicHolidays({
    years: publicHolidaysFromYears,
    ...passArgs,
  });

  const yearArraysMatch = useCallback((array1: number[], array2: number[]) => {
    return JSON.stringify(array1) === JSON.stringify(array2);
  }, []);

  const updateYears = useCallback(
    (years: number[]) => {
      const appendYears = years.filter(
        (y) => !publicHolidaysFromYears.includes(y),
      );

      if (appendYears.length > 0) {
        const newYears = [...publicHolidaysFromYears, ...appendYears].sort(
          (a, b) => a - b,
        );

        if (!yearArraysMatch(newYears, publicHolidaysFromYears)) {
          setPublicHolidaysFromYears(newYears);
        }
      }
    },
    [publicHolidaysFromYears, yearArraysMatch],
  );

  const setViewedRange = useCallback(
    (props: { pickerMonth: Date; pickerCalendars?: number }) => {
      const { pickerMonth, pickerCalendars } = props;

      updateYears(
        getYearsOfDatesInRange(
          pickerMonth,
          moment(pickerMonth)
            .add((!!pickerCalendars ? pickerCalendars : 2) - 1, 'month')
            .toDate(),
        ),
      );
    },
    [updateYears],
  );

  const setSelectedRange = useCallback(
    (props: { rangeStart: Date; rangeEnd: Date }) => {
      const { rangeStart, rangeEnd } = props;

      updateYears(getYearsOfDatesInRange(rangeStart, rangeEnd));
    },
    [updateYears],
  );

  return {
    publicHolidays: publicHolidays || [],
    setSelectedRange,
    setViewedRange,
    publicHolidaysFromYears,
  };
};
